import { getLicenseConfig, getPassportConfig, validateLicenseExpirationDate } from "components/drivers/show/formConfig";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { countrys } from "helpers/enums";
import { getInitialStateFromConfig } from "helpers/form";
import { requiredField } from "helpers/validators";
import moment from "moment";
import { selectList as cargoOwnersList } from "sources/books/cargoOwners";

const config = {
    counterpartyIds: {
        label: "Перевозчик(и)",
        inputProps: { name: "counterpartyIds" },
        url: cargoOwnersList,
        propName: "name",
        required: true,
        validators: [requiredField],
        menuPortalTarget: document.body,
        components: { ValueContainer },
        ...defaultMultipleSelectProps,
    },
    lastName: {
        label: "Фамилия",
        inputProps: { name: "lastName" },
        required: true,
        validators: [requiredField],
    },
    firstName: {
        label: "Имя",
        inputProps: { name: "firstName" },
        required: true,
        validators: [requiredField],
    },
    patronymic: {
        label: "Отчество",
        inputProps: { name: "patronymic" },
    },
    phoneNumber: {
        label: "Номер телефона",
        inputProps: { name: "phoneNumber" },
        required: true,
        validators: [requiredField],
        type: "phone",
    },
    passportIssuingCountry: {
        label: "Страна выдачи паспорта",
        inputProps: { name: "passportIssuingCountry" },
        options: countrys,
        placeholder: "Выберите страну",
        nullable: true,
    },
    passportSeries: {
        label: "Серия паспорта",
        inputProps: { name: "passportSeries", maxLength: 32 },
    },
    passportNumber: {
        label: "Номер паспорта",
        inputProps: { name: "passportNumber", maxLength: 32 },
    },
    passportIssuedBy: {
        label: "Кем выдан паспорт",
        inputProps: { name: "passportIssuedBy", maxLength: 256 },
    },
    passportDateOfIssue: {
        label: "Дата выдачи паспорта",
        inputProps: { name: "passportDateOfIssue", type: "date" },
    },
    passportSubdivisionCode: {
        label: "Код подразделения",
        inputProps: { name: "passportSubdivisionCode", maxLength: 32 },
    },
    licenseIssuingCountry: {
        label: "Страна выдачи водительского удостоверения",
        inputProps: { name: "licenseIssuingCountry" },
        options: countrys,
        placeholder: "Выберите страну",
        nullable: true,
    },
    licenseSeries: {
        label: "Серия водительского удостоверения",
        inputProps: { name: "licenseSeries", maxLength: 32 },
    },
    licenseNumber: {
        label: "Номер водительского удостоверения",
        inputProps: { name: "licenseNumber", maxLength: 32 },
    },
    licenseDateOfIssue: {
        label: "Дата выдачи водительского удостоверения",
        inputProps: { name: "licenseDateOfIssue", type: "date" },
    },
    licenseExpirationDate: {
        label: "Дата истечения срока действия водительского удостоверения",
        inputProps: { name: "licenseExpirationDate", type: "date" },
        validators: [validateLicenseExpirationDate],
    },
};

const optionalDocumentField = (source, target) => {
    const inputProps = {
        ...target.inputProps,
        ...source.inputProps,
        name: target.inputProps.name,
    };
    if (source.mask || source.type === "customMask") {
        delete inputProps.maxLength;
    }
    return {
        ...target,
        ...source,
        inputProps,
        label: target.label,
        required: false,
        validators: (source.validators || []).filter((validator) => validator !== requiredField),
    };
};

const getConfig = (data = {}) => {
    const passportCountry = data.passportIssuingCountry;
    const licenseCountry = data.licenseIssuingCountry;

    const passportFields = passportCountry
        ? {
              passportSeries: optionalDocumentField(getPassportConfig(passportCountry).series, config.passportSeries),
              passportNumber: optionalDocumentField(getPassportConfig(passportCountry).number, config.passportNumber),
              passportSubdivisionCode: optionalDocumentField(
                  getPassportConfig(passportCountry).subdivisionCode,
                  config.passportSubdivisionCode,
              ),
          }
        : {
              passportSeries: config.passportSeries,
              passportNumber: config.passportNumber,
              passportSubdivisionCode: config.passportSubdivisionCode,
          };

    const licenseFields = licenseCountry
        ? {
              licenseSeries: optionalDocumentField(
                  getLicenseConfig(licenseCountry, data.licenseSeries, data.licenseNumber).series,
                  config.licenseSeries,
              ),
              licenseNumber: optionalDocumentField(
                  getLicenseConfig(licenseCountry, data.licenseSeries, data.licenseNumber).number,
                  config.licenseNumber,
              ),
          }
        : {
              licenseSeries: config.licenseSeries,
              licenseNumber: config.licenseNumber,
          };

    const minLicenseDate =
        data.licenseDateOfIssue && moment(data.licenseDateOfIssue).isValid()
            ? moment(data.licenseDateOfIssue).toDate()
            : undefined;

    const licenseExpirationDateField = {
        ...config.licenseExpirationDate,
        validators: [
            (value, fieldData) =>
                validateLicenseExpirationDate(value, { licenseDateOfIssue: data.licenseDateOfIssue, ...fieldData }),
        ],
        libOptions: {
            isClearable: true,
            ...(minLicenseDate ? { minDate: minLicenseDate } : {}),
        },
    };

    return {
        ...config,
        ...passportFields,
        ...licenseFields,
        licenseExpirationDate: licenseExpirationDateField,
    };
};

const initialState = getInitialStateFromConfig(config);

export { config, getConfig, initialState };
